import React, { useEffect } from 'react';
import axios from 'axios';
import { isRouteErrorResponse, useLocation, useNavigate, useRouteError } from 'react-router-dom';
import { appRoutes } from '../../routes/appRoutes';
import IconFont from '../elements/IconFont';
import Toaster, { TriggerToast } from '../globals/Toaster';
import NotFound from './NotFound';
import { extensionMonitor } from '../../utils/monitoring';

const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    if (error.code === 'ECONNABORTED') {
      return 'Request timed out. Please try again.';
    }

    if (!error.response) {
      return 'Network request failed. Please check your connection.';
    }

    switch (error.response.status) {
      case 400:
        return 'The request could not be completed. Please check your input and try again.';
      case 403:
        return 'You do not have access to complete this action.';
      case 404:
        return 'We could not find the requested data.';
      case 409:
        return 'This action conflicts with the latest data.';
      case 422:
        return 'Some information is invalid. Please review and try again.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      default:
        if (error.response.status >= 500) {
          return 'Something went wrong on our side. Please try again.';
        }
        return 'Request failed. Please try again.';
    }
  }

  if (isRouteErrorResponse(error)) {
    return error.statusText || error.data?.message || 'The app could not load this view.';
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'The app could not load this view.';
};

const RouteErrorBoundary: React.FC = (): React.ReactElement => {
  const error = useRouteError();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    console.error('[RouteErrorBoundary]', error);
    if (!axios.isAxiosError(error) || !error.response || error.response.status >= 500) {
      extensionMonitor.capture(error);
    }
    if (axios.isAxiosError(error)) {
      TriggerToast(getErrorMessage(error), undefined, 'error');
    }
  }, [error]);

  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFound />;
  }

  if (axios.isAxiosError(error)) {
    return (
      <>
        <Toaster />
        <div className="flex min-h-screen items-center justify-center bg-background-surface-01 px-[24px]">
          <div className="flex w-full max-w-[420px] flex-col items-center text-center">
            <div className="mb-[16px] flex h-8 w-8 items-center justify-center rounded-[8px] bg-background-error text-light">
              <IconFont name="triangle-alert" size={16} className="text-light" />
            </div>
            <p className="text-sm font-medium text-body-muted">Request Error</p>
            <p className="mt-[4px] type-heading-sm text-heading">Unable to complete request</p>
            <p className="mt-[8px] text-sm text-body-muted">
              The request failed. A notification has been shown with the details.
            </p>
            <div className="mt-[12px] flex w-full flex-col gap-[8px] rounded-[6px] border border-border-default bg-background-surface-02 px-[12px] py-[8px] text-left">
              <p className="max-w-full break-words text-xs text-body-muted">
                <span className="font-medium text-heading">Path:</span> {location.pathname}
              </p>
              <p className="max-w-full break-words text-xs text-body-muted">
                <span className="font-medium text-heading">Error:</span> {getErrorMessage(error)}
              </p>
            </div>
            <div className="mt-[16px] flex w-full gap-[8px]">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => navigate(appRoutes.experiments.root, { replace: true })}
              >
                Dashboard
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => window.location.reload()}
              >
                Reload
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background-surface-01 px-[24px]">
      <div className="flex w-full max-w-[420px] flex-col items-center text-center">
        <div className="mb-[16px] flex h-8 w-8 items-center justify-center rounded-[8px] bg-background-error text-light">
          <IconFont name="triangle-alert" size={16} className="text-light" />
        </div>
        <p className="text-sm font-medium text-body-muted">Runtime Error</p>
        <p className="mt-[4px] type-heading-sm text-heading">Something went wrong</p>
        <p className="mt-[8px] text-sm text-body-muted">
          This page is available, but the app hit an error while loading it.
        </p>
        <div className="mt-[12px] flex w-full flex-col gap-[8px] rounded-[6px] border border-border-default bg-background-surface-02 px-[12px] py-[8px] text-left">
          <p className="max-w-full break-words text-xs text-body-muted">
            <span className="font-medium text-heading">Path:</span> {location.pathname}
          </p>
          <p className="max-w-full break-words text-xs text-body-muted">
            <span className="font-medium text-heading">Error:</span> {getErrorMessage(error)}
          </p>
        </div>
        <div className="mt-[16px] flex w-full gap-[8px]">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate(appRoutes.experiments.root, { replace: true })}
          >
            Dashboard
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </div>
      </div>
    </div>
  );
};

export default RouteErrorBoundary;
